
import { CommunicationLog, SystemAlert, CommunicationType, CommunicationStatus } from '@/types/communication';
import { enhancedToast } from '@/utils/notifications/enhancedToast';

class CommunicationService {
  private storageKey = 'nalc_communication_logs';
  private alertsKey = 'nalc_system_alerts';

  private async getLogs(): Promise<CommunicationLog[]> {
    const logs = localStorage.getItem(this.storageKey);
    return logs ? JSON.parse(logs) : [];
  }

  private async getAlerts(): Promise<SystemAlert[]> {
    const alerts = localStorage.getItem(this.alertsKey);
    return alerts ? JSON.parse(alerts) : [];
  }

  async logCommunication(log: Omit<CommunicationLog, 'id' | 'timestamp'>): Promise<CommunicationLog> {
    try {
      const logs = await this.getLogs();
      const newLog: CommunicationLog = {
        ...log,
        id: crypto.randomUUID(),
        timestamp: new Date().toISOString()
      };

      logs.push(newLog);
      localStorage.setItem(this.storageKey, JSON.stringify(logs));
      
      // Show success notification with sound
      enhancedToast.success('Communication logged successfully');
      
      return newLog;
    } catch (error) {
      console.error('Error logging communication:', error);
      enhancedToast.error('Failed to log communication');
      throw error;
    }
  }

  async addSystemAlert(alert: Omit<SystemAlert, 'id' | 'timestamp'>): Promise<SystemAlert> {
    try {
      const alerts = await this.getAlerts();
      const newAlert: SystemAlert = {
        ...alert,
        id: crypto.randomUUID(),
        timestamp: new Date().toISOString()
      };

      alerts.push(newAlert);
      localStorage.setItem(this.alertsKey, JSON.stringify(alerts));
      
      // Show alert notification with appropriate sound based on level
      if (alert.level === 'error' || alert.level === 'critical') {
        enhancedToast.error('System Alert Added', alert.message);
      } else if (alert.level === 'warning') {
        enhancedToast.warning('System Alert Added', alert.message);
      } else {
        enhancedToast.info('System Alert Added', alert.message);
      }
      
      return newAlert;
    } catch (error) {
      console.error('Error adding system alert:', error);
      enhancedToast.error('Failed to add system alert');
      throw error;
    }
  }

  async getCommunicationHistory(
    recipientId?: string,
    type?: CommunicationType
  ): Promise<CommunicationLog[]> {
    try {
      const logs = await this.getLogs();
      return logs.filter(log => 
        (!recipientId || log.recipientId === recipientId) &&
        (!type || log.type === type)
      );
    } catch (error) {
      console.error('Error getting communication history:', error);
      enhancedToast.error('Failed to retrieve communication history');
      throw error;
    }
  }

  async updateCommunicationStatus(
    logId: string, 
    status: CommunicationStatus
  ): Promise<void> {
    try {
      const logs = await this.getLogs();
      const logIndex = logs.findIndex(log => log.id === logId);
      
      if (logIndex === -1) {
        throw new Error('Communication log not found');
      }

      logs[logIndex].status = status;
      localStorage.setItem(this.storageKey, JSON.stringify(logs));
      
      // Show status update notification
      enhancedToast.success(`Communication status updated to ${status}`);
    } catch (error) {
      console.error('Error updating communication status:', error);
      enhancedToast.error('Failed to update communication status');
      throw error;
    }
  }
}

export const communicationService = new CommunicationService();
